import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import PCTMap from '../components/PCTMap';
import globalStyles from '../styles/global.module.css';
import styles from './Updates.module.css';
import apiConfig from '../config/api';
import { getHikeDay } from '../utils/dateUtils';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const UpdateDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    mile: 0,
    text: '',
    title: '',
    update_type: 'update',
    time: new Date().toISOString()
  });
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [message, setMessage] = useState('');
  const [fullScreenPhoto, setFullScreenPhoto] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // Convert newlines to <br> tags for proper rendering
  const formatTextWithLineBreaks = (text) => {
    return text.replace(/\n/g, '<br>');
  };

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await fetch(`${apiConfig.baseUrl}${apiConfig.endpoints.updates}/${id}`);
        
        if (!response.ok) {
          throw new Error('Post not found');
        }
        
        const data = await response.json();
        setPost(data);
        setFormData({
          mile: data.mile || 0,
          text: data.text || '',
          title: data.title || '',
          update_type: data.update_type || 'update',
          time: data.time || new Date().toISOString()
        });
      } catch (error) {
        console.error('Error fetching post:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'mile' ? Number(value) : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${apiConfig.baseUrl}${apiConfig.endpoints.updates}/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to update post');
      }

      const updatedPost = await response.json();
      setPost(updatedPost);
      setEditMode(false);
      setMessage('Update saved successfully');
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error updating post:', error);
      setMessage('Error updating post: ' + error.message);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${apiConfig.baseUrl}${apiConfig.endpoints.updates}/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to delete post');
      }

      navigate('/posts');
    } catch (error) {
      console.error('Error deleting post:', error);
      setMessage('Error deleting post: ' + error.message);
    }
  };

  const openFullScreen = (photoUrl, index) => {
    setFullScreenPhoto(photoUrl);
    setCurrentPhotoIndex(index);
    document.body.style.overflow = 'hidden';
  };

  const closeFullScreen = () => {
    setFullScreenPhoto(null);
    document.body.style.overflow = 'auto';
  };
  
  const navigatePhoto = (direction) => {
    if (!post || !post.photoUrls) return;
    
    let newIndex;
    if (direction === 'next') {
      newIndex = (currentPhotoIndex + 1) % post.photoUrls.length;
    } else {
      newIndex = (currentPhotoIndex - 1 + post.photoUrls.length) % post.photoUrls.length;
    }
    
    setCurrentPhotoIndex(newIndex);
    setFullScreenPhoto(`${apiConfig.baseUrl}${post.photoUrls[newIndex]}`);
  };
  
  const handleKeyDown = (e) => {
    if (!fullScreenPhoto) return;
    
    if (e.key === 'Escape') {
      closeFullScreen();
    } else if (e.key === 'ArrowRight') {
      navigatePhoto('next');
    } else if (e.key === 'ArrowLeft') {
      navigatePhoto('prev');
    }
  };
  
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [fullScreenPhoto, currentPhotoIndex, post]);

  return (
    <Layout>
      <div className={`${globalStyles.pageContainer} ${styles.wideContainer}`}>
        {loading ? (
          <p>Loading post...</p>
        ) : error ? (
          <div>
            <p>Error: {error}</p>
            <Link to="/posts" className={styles.backLink}>Back to updates</Link>
          </div>
        ) : post ? (
          <div>
            <div className={styles.headerControls}>
              <Link to="/posts" className={styles.backLink}>← Back to all updates</Link>
              {user && (
                <div className={styles.adminControls}>
                  {!editMode && !deleteConfirm && (
                    <>
                      <button 
                        onClick={() => setEditMode(true)} 
                        className={styles.editButton}
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => setDeleteConfirm(true)} 
                        className={styles.deleteButton}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            
            {message && (
              <div className={styles.statusMessage}>
                {message}
              </div>
            )}
            
            {deleteConfirm && (
              <div className={styles.confirmDelete}>
                <p>Are you sure you want to delete this update?</p>
                <div className={styles.confirmButtons}>
                  <button onClick={handleDelete} className={styles.confirmButton}>
                    Yes, Delete
                  </button>
                  <button onClick={() => setDeleteConfirm(false)} className={styles.cancelButton}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
            
            {editMode ? (
              <form onSubmit={handleSubmit} className={styles.editForm}>
                <div className={globalStyles.formGroup}>
                  <label htmlFor="mile">Mile</label>
                  <input
                    type="number"
                    id="mile"
                    name="mile"
                    className={globalStyles.input}
                    value={formData.mile}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className={globalStyles.formGroup}>
                  <label htmlFor="title">Title (optional)</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    className={globalStyles.input}
                    value={formData.title}
                    onChange={handleInputChange}
                  />
                </div>
                
                <div className={globalStyles.formGroup}>
                  <label htmlFor="text">Text</label>
                  <textarea
                    id="text"
                    name="text"
                    className={globalStyles.input}
                    value={formData.text}
                    onChange={handleInputChange}
                    required
                    rows="6"
                  />
                </div>
                
                <div className={globalStyles.formGroup}>
                  <label htmlFor="time">Date and Time</label>
                  <input
                    type="datetime-local"
                    id="time"
                    name="time"
                    className={globalStyles.input}
                    value={formData.time.split('.')[0]}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className={styles.formButtons}>
                  <button type="submit" className={styles.saveButton}>
                    Save Changes
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setEditMode(false)}
                    className={styles.cancelButton}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className={styles.singleColumnLayout}>
                <div className={styles.updateHeader}>
                  <div className={styles.headerWithMap}>
                    <div className={styles.headerInfo}>
                      <h2 className={styles.locationName}>
                        Day {getHikeDay(post.time)}
                        {post.title && `: ${post.title}`}
                      </h2>
                      
                      <div className={styles.prominentStats}>
                        <div className={styles.statItem}>
                          <span className={styles.statLabel}>Mile</span>
                          <span className={styles.statValue}>{post.mile}</span>
                        </div>
                        <div className={styles.statItem}>
                          <span className={styles.statLabel}>Date</span>
                          <span className={styles.statValue}>
                            {(() => {
                              const date = new Date(post.time);
                              const options = { 
                                year: 'numeric', 
                                month: '2-digit', 
                                day: '2-digit',
                                timeZone: 'America/Los_Angeles' 
                              };
                              const formatted = new Intl.DateTimeFormat('en-US', options).format(date);
                              const [month, day, year] = formatted.split('/');
                              return `${year}-${month}-${day}`;
                            })()}
                          </span>
                        </div>
                      </div>
                      
                      <div className={styles.mapContainer}>
                        <PCTMap 
                          latestUpdate={post}
                          milesCovered={post.mile}
                          totalMiles={2650}
                        />
                      </div>
                    </div>
                    
                    <div className={styles.floatGallery}>
                      {post.photoUrls && post.photoUrls.length > 0 && (
                        <div className={styles.galleryPreview}>
                          <img 
                            src={`${apiConfig.baseUrl}${post.photoUrls[0]}`}
                            alt={`Photo from day ${getHikeDay(post.time)}`}
                            className={styles.previewPhoto}
                            onClick={() => openFullScreen(`${apiConfig.baseUrl}${post.photoUrls[0]}`, 0)}
                          />
                          {post.photoUrls.length > 1 && (
                            <div className={styles.photoCount} onClick={() => document.getElementById('photoSection').scrollIntoView({ behavior: 'smooth' })}>
                              +{post.photoUrls.length - 1}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div 
                    className={styles.updateText}
                    dangerouslySetInnerHTML={{ __html: post.text ? formatTextWithLineBreaks(post.text) : '' }}
                  />
                </div>
                
                <div className={styles.contentSection} id="photoSection">
                  {post.photoUrls && post.photoUrls.length > 0 && (
                    <div className={styles.photoGrid}>
                      {post.photoUrls.map((photoUrl, index) => (
                        <img 
                          key={index}
                          src={`${apiConfig.baseUrl}${photoUrl}`}
                          alt={`Photo from day ${getHikeDay(post.time)}`}
                          className={styles.detailPhoto}
                          onClick={() => openFullScreen(`${apiConfig.baseUrl}${photoUrl}`, index)}
                          style={{ cursor: 'pointer' }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {fullScreenPhoto && (
              <div 
                className={styles.photoOverlay}
                onClick={closeFullScreen}
              >
                <div 
                  className={styles.photoOverlayContent}
                  onClick={(e) => e.stopPropagation()}
                >
                  <img 
                    src={fullScreenPhoto} 
                    alt="Full screen view" 
                    className={styles.fullScreenPhoto}
                  />
                  <button 
                    className={styles.closeButton}
                    onClick={closeFullScreen}
                  >
                    ×
                  </button>
                  
                  {post.photoUrls && post.photoUrls.length > 1 && (
                    <>
                      <button 
                        className={`${styles.navButton} ${styles.prevButton}`}
                        onClick={() => navigatePhoto('prev')}
                      >
                        &#10094;
                      </button>
                      <button 
                        className={`${styles.navButton} ${styles.nextButton}`}
                        onClick={() => navigatePhoto('next')}
                      >
                        &#10095;
                      </button>
                      <div className={styles.photoCounter}>
                        {currentPhotoIndex + 1} / {post.photoUrls.length}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p>Post not found</p>
        )}
      </div>
    </Layout>
  );
};

export default UpdateDetail; 